import { useUser } from "@clerk/clerk-expo";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 30_000;
const DEFAULT_QUERY_KEY = ["user-public-metadata"] as const;

type Options = {
	enabled?: boolean;
	queryKey?: readonly unknown[];
	timeoutMs?: number;
};

export const usePollUserPublicMetadata = ({
	enabled = true,
	queryKey = DEFAULT_QUERY_KEY,
	timeoutMs = POLL_TIMEOUT_MS,
}: Options = {}) => {
	const { user } = useUser();
	const { userLogicalId, userPublicMetadataId } = useAuthContext();
	const [hasTimedOut, setHasTimedOut] = useState(false);

	const resetTimeout = useCallback(() => {
		setHasTimedOut(false);
	}, []);

	const isWaitingForPublicMetadata =
		enabled && !!user && !userLogicalId && !hasTimedOut;

	useEffect(() => {
		if (!enabled) {
			setHasTimedOut(false);
		}
	}, [enabled]);

	useEffect(() => {
		if (!isWaitingForPublicMetadata) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setHasTimedOut(true);
			captureErrorToSentry(
				new Error("User public metadata polling timed out"),
				{
					queryKey: JSON.stringify(queryKey),
					timeoutMs,
				},
			);
		}, timeoutMs);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [isWaitingForPublicMetadata, queryKey, timeoutMs]);

	useQuery({
		queryKey,
		queryFn: async () => {
			const newUser = await user?.reload();
			return newUser ?? null;
		},
		enabled: isWaitingForPublicMetadata,
		refetchInterval: POLL_INTERVAL_MS,
	});

	return {
		userLogicalId,
		userPublicMetadataId,
		isWaitingForPublicMetadata,
		hasTimedOut,
		resetTimeout,
	};
};
